import React, { useEffect, useMemo, useRef, useState } from "react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { fetchPdfBlobUrl } from "@/lib/pdf";
import { toast } from "sonner";
import {
  FileText, Plus, Trash2, ArrowUp, ArrowDown, Save,
  Type, QrCode, Image as ImageIcon, Minus as MinusIcon, MoveVertical, Palette,
  AlignLeft, AlignCenter, AlignRight, Bold, Italic, RefreshCw, Printer,
  Copy, Star, X, Edit3, Table as TableIcon, Barcode, FileSignature, GripVertical,
} from "lucide-react";

const BLOCK_TYPES = [
  { v: "logo",      l: "Logo",       icon: ImageIcon },
  { v: "text",      l: "Text",       icon: Type },
  { v: "qr",        l: "QR-Code",    icon: QrCode },
  { v: "barcode",   l: "Barcode",    icon: Barcode },
  { v: "divider",   l: "Trennlinie", icon: MinusIcon },
  { v: "spacer",    l: "Abstand",    icon: MoveVertical },
  { v: "image",     l: "Bild",       icon: ImageIcon },
  { v: "table",     l: "Tabelle",    icon: TableIcon },
  { v: "signature", l: "TSE-Signatur", icon: FileSignature },
];

const SIZE_PX = { xs: 10, sm: 12, md: 14, lg: 20, xl: 28, xxl: 40 };
const SIZE_LABELS = [
  { v: "xs", l: "XS" }, { v: "sm", l: "S" }, { v: "md", l: "M" },
  { v: "lg", l: "L" },  { v: "xl", l: "XL" }, { v: "xxl", l: "XXL" },
];

const PAPER_PRESETS = [
  { v: "",              l: "Frei (Breite in mm)" },
  { v: "a4",            l: "A4 · Hochkant" },
  { v: "a5",            l: "A5 · Hochkant" },
  { v: "a5_landscape",  l: "A5 · Querformat" },
];

// Placeholders je Doku-Typ (was du im Text als {name} nutzen kannst)
const PLACEHOLDERS = {
  voucher:         ["cinema", "slogan", "code", "amount", "to", "from", "message", "valid_until"],
  receipt_a4:      ["cinema", "cinema_address", "cinema_phone", "cinema_vat_id", "order_no", "date", "guest", "movie", "payment", "total", "subtotal", "net", "tax", "tax_rate", "tse_counter", "tse_short"],
  receipt_thermal: ["cinema", "cinema_address", "cinema_phone", "order_no", "date", "guest", "payment", "total", "tse_counter", "tse_short"],
  invoice_a4:      ["cinema", "cinema_address", "cinema_phone", "cinema_email", "cinema_vat_id", "order_no", "date", "guest", "payment", "total", "net", "tax", "tax_rate", "tse_counter", "tse_short"],
  marathon:        ["cinema", "slogan", "code", "guest", "series", "seats", "price", "show_count"],
  club_card:       ["cinema", "guest", "member_number", "tier", "visits", "qr_url"],
};

function interp(str, data) {
  return String(str || "").replace(/\{(\w+)\}/g, (_, k) => (data[k] ?? `{${k}}`));
}

function sampleData(kind) {
  const common = { cinema: "THE MOVIE HOUSE", slogan: "More Than Watching Movies.",
    cinema_address: "Kinostr. 1 · 12345 Beispielstadt", cinema_phone: "+49 30 1234567",
    cinema_email: "hallo@movie-house.de", cinema_vat_id: "DE123456789" };
  if (kind === "voucher") return { ...common, code: "MH-ABC-123456", amount: "50,00",
    to: "Anna Schmitt", from: "Papa", message: "Frohes Fest!", valid_until: "31.12.2027" };
  if (kind === "marathon") return { ...common, code: "MH-MRT-987654", guest: "Max Mustermann",
    series: "Herr der Ringe · Extended", seats: "2", price: "36,00 €", show_count: "3",
    shows: [
      { date: "12.10.2026", start_time: "20:00", hall: "Saal 1" },
      { date: "13.10.2026", start_time: "20:00", hall: "Saal 1" },
      { date: "14.10.2026", start_time: "20:00", hall: "Saal 1" },
    ]};
  if (kind === "club_card") return { ...common, guest: "Erika Musterfrau",
    member_number: "MH-000247", tier: "GOLD", visits: "12",
    qr_url: "https://home-cinema-system.emergent.host/club/MH-000247" };
  return { ...common, order_no: "20260805-0007", date: "05.08.2026 20:15",
    guest: "Max Mustermann", movie: "Interstellar", payment: "KARTE (Stripe)",
    total: "29,50 €", subtotal: "29,50 €", net: "24,79 €", tax: "4,71 €", tax_rate: "19,0",
    tse_counter: "1273", tse_short: "a7c8d29e",
    items: [
      { quantity: 2, name: "Kinoticket · Interstellar", unit: "8,00 €", total: "16,00 €" },
      { quantity: 1, name: "Popcorn Groß",             unit: "6,50 €", total: "6,50 €" },
      { quantity: 2, name: "Cola 0,5 L",               unit: "3,50 €", total: "7,00 €" },
    ]};
}

// ---- Block-Vorschau als HTML (grobe Näherung – Original-PDF via Preview-Button) ----
function BlockPreview({ block, design, data }) {
  const align = { left: "text-left", center: "text-center", right: "text-right" }[block.align || "center"] || "text-center";
  const style = { color: block.color || design.foreground || "#0A0A0A",
    fontSize: SIZE_PX[block.size || "sm"], fontWeight: block.bold ? 700 : 400,
    fontStyle: block.italic ? "italic" : "normal",
    paddingTop: block.padding_top || 0, paddingBottom: block.padding_bottom || 0, lineHeight: 1.3 };
  if (block.type === "text") return <div className={align} style={style}>{interp(block.content, data) || <span className="opacity-30">(leer)</span>}</div>;
  if (block.type === "logo") {
    const url = design.logo_url; const maxW = design.logo_max_width || 180;
    if (!url) return <div className={align + " opacity-40"} style={{ ...style, fontStyle: "italic", fontSize: 10 }}>[Logo · URL im Panel setzen]</div>;
    return <div className={align} style={{ padding: `${block.padding_top || 0}px 0 ${block.padding_bottom || 0}px` }}>
      <img src={url} alt="" style={{ maxWidth: maxW, height: "auto", display: "inline-block" }} />
    </div>;
  }
  if (block.type === "image" && block.url) {
    return <div className={align} style={{ padding: `${block.padding_top || 0}px 0 ${block.padding_bottom || 0}px` }}>
      <img src={block.url} alt="" style={{ maxWidth: block.max_width || 200, height: "auto", display: "inline-block" }} />
    </div>;
  }
  if (block.type === "qr") {
    const size = block.size || design.qr_size || 140;
    return <div className={align} style={{ padding: `${block.padding_top || 0}px 0 ${block.padding_bottom || 0}px` }}>
      <div style={{ display: "inline-block", background: "#FFFFFF", padding: 4 }}>
        <div style={{ width: size, height: size, background: "repeating-conic-gradient(#000 0 25%, #fff 0 50%)", backgroundSize: "6px 6px" }} />
      </div>
    </div>;
  }
  if (block.type === "barcode") {
    const h = block.height || 60;
    return <div style={{ padding: `${block.padding_top || 0}px 0 ${block.padding_bottom || 0}px` }}>
      <div style={{ height: h, width: "100%", backgroundImage: "linear-gradient(90deg, #000 2px, #fff 2px, #fff 4px, #000 4px, #000 5px, #fff 5px, #fff 8px)", backgroundSize: "8px 100%" }} />
    </div>;
  }
  if (block.type === "divider") {
    const st = block.style === "solid" ? "solid" : (block.style === "dotted" ? "dotted" : "dashed");
    return <div style={{ padding: `${block.padding_top || 0}px 0 ${block.padding_bottom || 0}px` }}>
      <div style={{ borderTop: `1px ${st} ${block.color || design.foreground || "#0A0A0A"}`, opacity: 0.7 }} />
    </div>;
  }
  if (block.type === "spacer") return <div style={{ height: (block.padding_top || 0) + (block.padding_bottom || 0) + (block.height || 8) }} />;
  if (block.type === "signature") {
    return <div className="text-center font-mono text-[10px] opacity-60" style={{ padding: `${block.padding_top || 0}px 0 ${block.padding_bottom || 0}px` }}>
      {interp(block.content || "TSE #{tse_counter} · {tse_short} · KassenSichV", data)}
    </div>;
  }
  if (block.type === "table") {
    const rows = data[block.source || "items"] || [];
    const cols = block.columns || [
      { field: "quantity", label: "Menge", width: 0.12, align: "right" },
      { field: "name",     label: "Position", width: 0.58, align: "left" },
      { field: "unit",     label: "Einzel", width: 0.15, align: "right" },
      { field: "total",    label: "Summe", width: 0.15, align: "right" },
    ];
    return <table className="w-full text-xs" style={{ padding: `${block.padding_top || 0}px 0 ${block.padding_bottom || 0}px`, borderCollapse: "collapse" }}>
      {block.show_header !== false && <thead><tr style={{ borderBottom: `1px solid ${design.foreground}` }}>
        {cols.map((c, i) => <th key={i} style={{ textAlign: c.align || "left", padding: "2px 4px", fontSize: 10 }}>{c.label || c.field}</th>)}
      </tr></thead>}
      <tbody>{rows.map((r, i) => <tr key={i}>
        {cols.map((c, ci) => <td key={ci} style={{ textAlign: c.align || "left", padding: "2px 4px", fontSize: 10 }}>{r[c.field] ?? ""}</td>)}
      </tr>)}</tbody>
    </table>;
  }
  return null;
}

export default function DocumentDesigner() {
  const [kinds, setKinds] = useState([]);
  const [kind, setKind] = useState("voucher");
  const [templates, setTemplates] = useState([]);
  const [activeId, setActiveId] = useState(null);
  const [design, setDesign] = useState(null);
  const [selectedId, setSelectedId] = useState(null);
  const [busy, setBusy] = useState(false);
  const [previewUrl, setPreviewUrl] = useState(null);
  const [dragOverIdx, setDragOverIdx] = useState(null);
  const dragIdRef = useRef(null);

  useEffect(() => { api.get("/admin/doc-templates/kinds").then((r) => setKinds(r.data)).catch(() => {}); }, []);

  const loadTemplates = async (kindKey, keepId) => {
    try {
      const { data } = await api.get(`/admin/doc-templates?kind=${kindKey}`);
      setTemplates(data);
      const nextId = keepId && data.find((t) => t.id === keepId)
        ? keepId : (data.find((t) => t.is_default)?.id || data[0]?.id);
      setActiveId(nextId);
    } catch (e) { toast.error("Vorlagen konnten nicht geladen werden"); }
  };
  useEffect(() => { loadTemplates(kind); }, [kind]);

  useEffect(() => {
    if (!activeId) { setDesign(null); return; }
    const t = templates.find((x) => x.id === activeId);
    if (t) { setDesign(JSON.parse(JSON.stringify(t))); setSelectedId(t.blocks?.[0]?.id || null); }
  }, [activeId, templates]);

  const activeTmpl = useMemo(() => templates.find((t) => t.id === activeId) || null, [templates, activeId]);
  const selected = design?.blocks?.find((b) => b.id === selectedId) || null;
  const isDirty = useMemo(() => {
    if (!design || !activeTmpl) return false;
    return JSON.stringify({ ...design, _id: undefined }) !== JSON.stringify({ ...activeTmpl, _id: undefined });
  }, [design, activeTmpl]);

  const data = useMemo(() => sampleData(kind), [kind]);

  const updateDesign = (patch) => setDesign((d) => ({ ...d, ...patch }));
  const updateBlock = (id, patch) => setDesign((d) => ({ ...d, blocks: d.blocks.map((b) => b.id === id ? { ...b, ...patch } : b) }));
  const addBlock = (type) => {
    const id = `b${Date.now()}${Math.floor(Math.random() * 999)}`;
    const defaults = { logo: { padding_bottom: 8 }, text: { size: "sm", align: "center", content: "Neuer Text", padding_bottom: 4 },
      qr: { padding_bottom: 6 }, barcode: { height: 60, padding_bottom: 6 }, divider: { style: "dashed", padding_bottom: 6 },
      spacer: { height: 8 }, image: { url: "", max_width: 180, padding_bottom: 6 },
      table: { source: "items", show_header: true, row_height: 14, columns: [
        { field: "quantity", label: "Menge", width: 0.12, align: "right", format: "int" },
        { field: "name",     label: "Position", width: 0.58, align: "left" },
        { field: "unit",     label: "Einzel", width: 0.15, align: "right", format: "eur" },
        { field: "total",    label: "Gesamt", width: 0.15, align: "right", format: "eur" },
      ], padding_bottom: 8 },
      signature: { content: "TSE #{tse_counter} · {tse_short} · KassenSichV", font_size: 7 },
    };
    const blk = { id, type, align: "center", ...defaults[type] };
    setDesign((d) => ({ ...d, blocks: [...d.blocks, blk] }));
    setSelectedId(id);
  };
  const removeBlock = (id) => {
    setDesign((d) => ({ ...d, blocks: d.blocks.filter((b) => b.id !== id) }));
    if (selectedId === id) setSelectedId(null);
  };
  const move = (id, dir) => setDesign((d) => {
    const idx = d.blocks.findIndex((b) => b.id === id);
    const target = idx + dir;
    if (idx < 0 || target < 0 || target >= d.blocks.length) return d;
    const arr = [...d.blocks]; [arr[idx], arr[target]] = [arr[target], arr[idx]];
    return { ...d, blocks: arr };
  });
  const onDrop = (targetIdx) => {
    const dragId = dragIdRef.current; dragIdRef.current = null; setDragOverIdx(null);
    if (!dragId) return;
    setDesign((d) => {
      const from = d.blocks.findIndex((b) => b.id === dragId);
      if (from < 0 || from === targetIdx) return d;
      const arr = [...d.blocks];
      const [moved] = arr.splice(from, 1);
      arr.splice(from < targetIdx ? targetIdx - 1 : targetIdx, 0, moved);
      return { ...d, blocks: arr };
    });
  };

  const saveActive = async () => {
    if (!design) return;
    setBusy(true);
    try {
      await api.patch(`/admin/doc-templates/${design.id}`, design);
      toast.success(`„${design.name}" gespeichert`);
      await loadTemplates(kind, design.id);
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Speichern fehlgeschlagen"); }
    finally { setBusy(false); }
  };
  const createNew = async () => {
    const name = window.prompt("Name der neuen Vorlage:", "Neue Vorlage");
    if (!name?.trim()) return;
    try {
      const { data } = await api.post("/admin/doc-templates", { ...design, id: undefined, _id: undefined, name: name.trim(), is_default: false, doc_kind: kind });
      toast.success(`„${data.name}" angelegt`);
      await loadTemplates(kind, data.id);
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Fehler"); }
  };
  const duplicateActive = async () => {
    if (!activeId) return;
    try {
      const { data } = await api.post(`/admin/doc-templates/${activeId}/duplicate`);
      toast.success(`Kopie „${data.name}" angelegt`);
      await loadTemplates(kind, data.id);
    } catch (e) { toast.error("Duplizieren fehlgeschlagen"); }
  };
  const deleteActive = async () => {
    if (!activeTmpl || !window.confirm(`Vorlage „${activeTmpl.name}" wirklich löschen?`)) return;
    try {
      await api.delete(`/admin/doc-templates/${activeId}`);
      toast.success("Gelöscht"); await loadTemplates(kind);
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Fehler"); }
  };
  const setAsDefault = async () => {
    if (!activeId) return;
    try {
      await api.post(`/admin/doc-templates/${activeId}/set-default`);
      toast.success("Als Standard gesetzt"); await loadTemplates(kind, activeId);
    } catch (e) { toast.error("Fehler"); }
  };
  const refreshPreview = async () => {
    if (!activeId) return;
    // Für Preview vorher speichern
    if (isDirty) await saveActive();
    // Blob-URL statt direkter URL — sendet Bearer-Token, verhindert 401 im iframe
    try {
      const url = await fetchPdfBlobUrl(`/admin/doc-templates/${activeId}/preview.pdf?t=${Date.now()}`);
      setPreviewUrl((old) => { if (old && old.startsWith("blob:")) URL.revokeObjectURL(old); return url; });
    } catch {
      toast.error("Vorschau konnte nicht geladen werden");
    }
  };

  if (!kinds.length) return <div className="text-[#575148] text-center py-20">Lade Doku-Typen …</div>;
  if (!design) return <div className="text-[#575148] text-center py-20">Lade Vorlagen …</div>;

  const kindLabel = kinds.find((k) => k.kind === kind)?.label || kind;
  const placeholders = PLACEHOLDERS[kind] || [];

  return (
    <div className="space-y-6" data-testid="doc-designer-page">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div className="flex items-center gap-3">
          <FileText size={22} className="text-[#D4AF37]" />
          <div>
            <h1 className="font-heading text-3xl">Dokumenten-Designer</h1>
            <div className="text-[10px] uppercase tracking-[0.32em] text-[#A8A398] mt-1">Gutscheine · Rechnungen · Kassenbelege · Thermobons — alle mit Vorlagen</div>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <button onClick={refreshPreview} data-testid="doc-preview-btn"
            className="inline-flex items-center gap-2 px-4 py-2 border border-[#2A2A2A] rounded text-[11px] uppercase tracking-widest hover:border-[#D4AF37]">
            <RefreshCw size={12} /> PDF-Vorschau
          </button>
          <button onClick={async () => {
              if (!activeId) return;
              if (isDirty) await saveActive();
              try {
                const { data } = await api.post(`/admin/doc-templates/${activeId}/print`, {});
                toast.success(`Testdruck via WLAN-Drucker gesendet · ${data.printer}`);
              } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Druck fehlgeschlagen"); }
            }} data-testid="doc-wifi-print-btn"
            className="inline-flex items-center gap-2 px-4 py-2 border border-[#2A2A2A] rounded text-[11px] uppercase tracking-widest hover:border-[#D4AF37]">
            <Printer size={12} /> WLAN-Testdruck
          </button>
          <button onClick={saveActive} disabled={busy || !isDirty} data-testid="doc-save-btn"
            className={`inline-flex items-center gap-2 px-5 py-2 rounded text-[11px] uppercase tracking-widest disabled:opacity-40 ${isDirty ? "bg-[#D4AF37] hover:bg-[#EEDC82] text-black" : "border border-[#2A2A2A] text-[#575148]"}`}>
            <Save size={12} /> {busy ? "…" : isDirty ? "Änderungen speichern" : "Gespeichert"}
          </button>
        </div>
      </div>

      {/* Kind-Selector */}
      <div className="card-lux p-3">
        <div className="flex items-center gap-2 flex-wrap" data-testid="doc-kind-picker">
          {kinds.map((k) => (
            <button key={k.kind} onClick={() => {
              if (isDirty && !window.confirm("Ungespeicherte Änderungen verwerfen?")) return;
              setKind(k.kind);
            }} data-testid={`doc-kind-${k.kind}`}
              className={`px-3 py-2 rounded border text-sm ${kind === k.kind ? "border-[#D4AF37] bg-[#D4AF37]/[0.08] text-[#EEDC82]" : "border-[#2A2A2A] text-[#A8A398] hover:border-[#D4AF37]/60"}`}>
              {k.label}
            </button>
          ))}
        </div>
      </div>

      {/* Template-Picker */}
      <div className="card-lux p-3">
        <div className="flex items-center gap-2 flex-wrap">
          {templates.map((t) => (
            <button key={t.id} onClick={() => {
              if (isDirty && !window.confirm("Ungespeicherte Änderungen verwerfen?")) return;
              setActiveId(t.id);
            }} data-testid={`doc-tab-${t.id}`}
              className={`inline-flex items-center gap-2 px-3 py-2 rounded border text-sm ${t.id === activeId ? "border-[#D4AF37] bg-[#D4AF37]/[0.08] text-[#EEDC82]" : "border-[#2A2A2A] text-[#A8A398] hover:border-[#D4AF37]/60"}`}>
              {t.is_default && <Star size={12} className="text-[#D4AF37]" fill="#D4AF37" />}
              <span>{t.name}</span>
            </button>
          ))}
          <div className="mx-2 h-6 w-px bg-[#1F1F1F]" />
          <button onClick={createNew} className="inline-flex items-center gap-1 px-3 py-2 text-[10px] uppercase tracking-widest border border-[#D4AF37]/40 text-[#D4AF37] rounded hover:bg-[#D4AF37]/10">
            <Plus size={12} /> Neue Vorlage
          </button>
          <button onClick={duplicateActive} className="inline-flex items-center gap-1 px-3 py-2 text-[10px] uppercase tracking-widest border border-[#2A2A2A] text-[#A8A398] rounded hover:border-[#D4AF37] hover:text-[#D4AF37]">
            <Copy size={12} /> Duplizieren
          </button>
          {!activeTmpl?.is_default && (
            <button onClick={setAsDefault} className="inline-flex items-center gap-1 px-3 py-2 text-[10px] uppercase tracking-widest border border-[#8B7328] text-[#EEDC82] rounded hover:bg-[#D4AF37]/10">
              <Star size={12} /> Als Standard
            </button>
          )}
          <button onClick={deleteActive} disabled={templates.length <= 1}
            className="inline-flex items-center gap-1 px-3 py-2 text-[10px] uppercase tracking-widest border border-[#7A1921]/40 text-[#EE9CA1] rounded hover:bg-[#7A1921]/20 disabled:opacity-30 disabled:cursor-not-allowed">
            <Trash2 size={12} /> Löschen
          </button>
        </div>
        <div className="mt-2 pt-2 border-t border-[#1A1A1A] text-[10px] uppercase tracking-widest text-[#575148]">
          Aktive Vorlage: <span className="text-[#EEDC82] normal-case">{design.name}</span> · Typ: <span className="text-[#EEDC82] normal-case">{kindLabel}</span>
          {activeTmpl?.is_default && <span className="text-[#D4AF37] ml-2">★ Standard</span>}
          {isDirty && <span className="text-[#EE9CA1] ml-2">· Änderungen nicht gespeichert</span>}
        </div>
      </div>

      <div className="grid xl:grid-cols-[300px_1fr_360px] gap-5">
        {/* Spalte 1: Papier & Farben */}
        <div className="space-y-4">
          <div className="card-lux p-4">
            <div className="text-[10px] uppercase tracking-[0.32em] text-[#D4AF37] mb-3 flex items-center gap-2"><Palette size={12} /> Papier & Farben</div>
            <div className="space-y-3 text-sm">
              <div>
                <label className="text-[10px] uppercase tracking-widest text-[#A8A398]">Papier-Vorlage</label>
                <select value={design.paper_preset || ""} onChange={(e) => updateDesign({ paper_preset: e.target.value })} data-testid="doc-paper-preset"
                  className="w-full mt-1 bg-[#050505] border border-[#2A2A2A] rounded px-2 py-1.5 text-sm">
                  {PAPER_PRESETS.map((p) => <option key={p.v} value={p.v}>{p.l}</option>)}
                </select>
              </div>
              {!design.paper_preset && (
                <>
                  <RangeInput label="Breite (mm)" min={50} max={297} step={2} value={design.paper_width_mm} onChange={(v) => updateDesign({ paper_width_mm: v })} />
                  <RangeInput label="Höhe (mm, 0 = auto)" min={0} max={420} step={5} value={design.paper_height_mm || 0} onChange={(v) => updateDesign({ paper_height_mm: v })} />
                </>
              )}
              <RangeInput label="Rand (px)" min={0} max={100} step={2} value={design.margin_px || 24} onChange={(v) => updateDesign({ margin_px: v })} />
              <ColorInput label="Text" value={design.foreground} onChange={(v) => updateDesign({ foreground: v })} />
              <ColorInput label="Hintergrund" value={design.background} onChange={(v) => updateDesign({ background: v })} />
              <ColorInput label="Akzent" value={design.accent_color} onChange={(v) => updateDesign({ accent_color: v })} />
              <label className="flex items-center gap-2 cursor-pointer text-xs">
                <input type="checkbox" checked={!!design.show_border} onChange={(e) => updateDesign({ show_border: e.target.checked })} className="accent-[#D4AF37]" />
                Rahmen zeichnen
              </label>
            </div>
          </div>
          <div className="card-lux p-4">
            <div className="text-[10px] uppercase tracking-[0.32em] text-[#D4AF37] mb-3 flex items-center gap-2"><ImageIcon size={12} /> Logo</div>
            <input value={design.logo_url || ""} onChange={(e) => updateDesign({ logo_url: e.target.value })} placeholder="URL zum Logo (leer = Settings-Logo)"
              className="w-full bg-[#050505] border border-[#2A2A2A] rounded px-3 py-2 text-xs font-mono focus:border-[#D4AF37] outline-none" />
            <RangeInput label="Max. Logo-Breite (px)" min={40} max={500} step={5} value={design.logo_max_width || 180} onChange={(v) => updateDesign({ logo_max_width: v })} />
          </div>
          <div className="card-lux p-4">
            <div className="text-[10px] uppercase tracking-widest text-[#A8A398] mb-2">Platzhalter (klick zum Kopieren)</div>
            <div className="flex flex-wrap gap-1">
              {placeholders.map((p) => (
                <button key={p} onClick={() => { navigator.clipboard.writeText(`{${p}}`); toast.success(`{${p}} kopiert`); }}
                  className="px-2 py-0.5 text-[10px] font-mono border border-[#2A2A2A] rounded hover:border-[#D4AF37] text-[#EEDC82]">
                  {`{${p}}`}
                </button>
              ))}
            </div>
          </div>
        </div>

        {/* Spalte 2: Blöcke */}
        <div className="card-lux p-4">
          <div className="text-[10px] uppercase tracking-[0.32em] text-[#D4AF37] mb-3">Blöcke ({design.blocks.length})</div>
          <div className="space-y-1.5" data-testid="doc-block-list">
            {design.blocks.map((b, i) => {
              const meta = BLOCK_TYPES.find((t) => t.v === b.type);
              const Icon = meta?.icon || Type;
              const isSel = selectedId === b.id;
              return (
                <React.Fragment key={b.id}>
                  {dragOverIdx === i && <div className="h-0.5 bg-[#D4AF37] rounded" />}
                  <div draggable onDragStart={() => { dragIdRef.current = b.id; }}
                    onDragOver={(e) => { e.preventDefault(); setDragOverIdx(i); }} onDragLeave={() => setDragOverIdx(null)}
                    onDrop={() => onDrop(i)} onClick={() => setSelectedId(b.id)}
                    data-testid={`doc-block-${b.id}`}
                    className={`flex items-center gap-2 px-3 py-2 rounded border cursor-pointer select-none ${isSel ? "border-[#D4AF37] bg-[#D4AF37]/[0.06]" : "border-[#1F1F1F] hover:border-[#2A2A2A] bg-[#050505]"}`}>
                    <GripVertical size={13} className="text-[#575148] cursor-grab" />
                    <span className="text-[9px] font-mono text-[#575148] w-6">{i + 1}.</span>
                    <Icon size={13} className="text-[#D4AF37] shrink-0" />
                    <span className="text-[10px] uppercase tracking-widest text-[#EEDC82] w-14 shrink-0">{meta?.l}</span>
                    <span className="text-xs text-[#A8A398] truncate flex-1">
                      {b.type === "text" ? (b.content || "(leer)") : b.type === "table" ? `Tabelle (${b.source})` : `· ${meta?.l}`}
                    </span>
                    <div className="flex gap-0.5" onClick={(e) => e.stopPropagation()}>
                      <button onClick={() => move(b.id, -1)} disabled={i === 0} className="p-1 border border-[#2A2A2A] rounded hover:border-[#D4AF37] disabled:opacity-30"><ArrowUp size={11} /></button>
                      <button onClick={() => move(b.id, +1)} disabled={i === design.blocks.length - 1} className="p-1 border border-[#2A2A2A] rounded hover:border-[#D4AF37] disabled:opacity-30"><ArrowDown size={11} /></button>
                      <button onClick={() => removeBlock(b.id)} className="p-1 border border-[#2A2A2A] rounded hover:border-[#7A1921]"><Trash2 size={11} /></button>
                    </div>
                  </div>
                </React.Fragment>
              );
            })}
          </div>
          <div className="mt-4 pt-4 border-t border-[#1A1A1A]">
            <div className="text-[10px] uppercase tracking-widest text-[#A8A398] mb-2">Block hinzufügen</div>
            <div className="flex flex-wrap gap-1.5">
              {BLOCK_TYPES.map((t) => (
                <button key={t.v} onClick={() => addBlock(t.v)} data-testid={`doc-add-${t.v}`}
                  className="inline-flex items-center gap-1.5 px-3 py-1.5 border border-[#2A2A2A] hover:border-[#D4AF37] rounded text-[10px] uppercase tracking-widest text-[#A8A398] hover:text-[#D4AF37]">
                  <t.icon size={11} /> {t.l}
                </button>
              ))}
            </div>
          </div>

          {selected && <BlockPropsEditor block={selected} updateBlock={updateBlock} placeholders={placeholders} />}
        </div>

        {/* Spalte 3: Live-Vorschau */}
        <div className="space-y-3 xl:sticky xl:top-4 self-start">
          <div className="text-[10px] uppercase tracking-[0.32em] text-[#D4AF37]">Live-Vorschau · Beispieldaten</div>
          <div className="rounded p-4" style={{ background: "#181513" }}>
            <div className="mx-auto shadow-2xl overflow-hidden" data-testid="doc-preview"
              style={{ width: `${(design.paper_preset === "a4" ? 210 : design.paper_preset === "a5_landscape" ? 210 : design.paper_width_mm) * 2.5}px`, maxWidth: "100%",
                background: design.background || "#FFFFFF", color: design.foreground || "#0A0A0A",
                padding: `${design.margin_px || 24}px`,
                border: design.show_border ? `1px solid ${design.foreground || "#0A0A0A"}20` : "none",
                fontFamily: 'ui-monospace, Menlo, monospace' }}>
              {design.blocks.map((b) => <BlockPreview key={b.id} block={b} design={design} data={data} />)}
            </div>
          </div>
          {previewUrl && (
            <div className="card-lux p-2">
              <div className="text-[10px] uppercase tracking-widest text-[#A8A398] mb-2">PDF-Preview</div>
              <iframe src={previewUrl} title="preview" className="w-full h-[600px] bg-white rounded" />
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

// ---- Block-Props Editor -------------------------------------------------

function BlockPropsEditor({ block, updateBlock, placeholders }) {
  const insertPh = (ph) => {
    if (block.type !== "text" && block.type !== "signature") return;
    updateBlock(block.id, { content: `${block.content || ""}{${ph}}` });
  };
  return (
    <div className="mt-5 pt-4 border-t border-[#1A1A1A]">
      <div className="text-[10px] uppercase tracking-[0.32em] text-[#D4AF37] mb-3">Eigenschaften · {BLOCK_TYPES.find((t) => t.v === block.type)?.l}</div>
      {(block.type === "text" || block.type === "signature") && (
        <div className="space-y-3">
          <div>
            <label className="text-[10px] uppercase tracking-widest text-[#A8A398]">Inhalt (Platzhalter: {"{name}"})</label>
            <textarea rows={2} value={block.content || ""} onChange={(e) => updateBlock(block.id, { content: e.target.value })}
              data-testid="doc-text-content"
              className="w-full mt-1 bg-[#050505] border border-[#2A2A2A] rounded px-3 py-2 text-sm focus:border-[#D4AF37] outline-none font-mono" />
          </div>
          <div className="flex flex-wrap gap-1">
            {placeholders.map((ph) => (
              <button key={ph} onClick={() => insertPh(ph)}
                className="px-2 py-0.5 text-[10px] font-mono border border-[#2A2A2A] rounded hover:border-[#D4AF37] text-[#EEDC82]">{`{${ph}}`}</button>
            ))}
          </div>
        </div>
      )}
      {block.type === "text" && (
        <div className="mt-3 grid grid-cols-2 gap-2">
          <div>
            <label className="text-[10px] uppercase tracking-widest text-[#A8A398]">Größe</label>
            <select value={block.size || "sm"} onChange={(e) => updateBlock(block.id, { size: e.target.value })}
              className="w-full mt-1 bg-[#050505] border border-[#2A2A2A] rounded px-2 py-1.5 text-sm">
              {SIZE_LABELS.map((s) => <option key={s.v} value={s.v}>{s.l}</option>)}
            </select>
          </div>
          <div className="flex items-end gap-1">
            <button onClick={() => updateBlock(block.id, { bold: !block.bold })}
              className={`p-2 border rounded ${block.bold ? "border-[#D4AF37] text-[#D4AF37] bg-[#D4AF37]/10" : "border-[#2A2A2A] text-[#A8A398]"}`}><Bold size={13} /></button>
            <button onClick={() => updateBlock(block.id, { italic: !block.italic })}
              className={`p-2 border rounded ${block.italic ? "border-[#D4AF37] text-[#D4AF37] bg-[#D4AF37]/10" : "border-[#2A2A2A] text-[#A8A398]"}`}><Italic size={13} /></button>
            <input type="color" value={block.color || "#000000"} onChange={(e) => updateBlock(block.id, { color: e.target.value })}
              className="w-10 h-9 rounded bg-transparent border border-[#2A2A2A] cursor-pointer ml-auto" />
          </div>
        </div>
      )}
      {(block.type === "qr" || block.type === "barcode") && (
        <div className="mt-3 space-y-2">
          <div>
            <label className="text-[10px] uppercase tracking-widest text-[#A8A398]">Wert (Platzhalter erlaubt)</label>
            <input value={block.value || ""} placeholder={block.type === "qr" ? "{qr_url} oder {code}" : "{code}"}
              onChange={(e) => updateBlock(block.id, { value: e.target.value })}
              className="w-full mt-1 bg-[#050505] border border-[#2A2A2A] rounded px-3 py-2 text-xs font-mono focus:border-[#D4AF37] outline-none" />
          </div>
          {block.type === "qr" && <RangeInput label="Größe (px)" min={60} max={260} step={10} value={block.size || 140} onChange={(v) => updateBlock(block.id, { size: v })} />}
          {block.type === "barcode" && <RangeInput label="Höhe (px)" min={20} max={140} step={5} value={block.height || 60} onChange={(v) => updateBlock(block.id, { height: v })} />}
        </div>
      )}
      {block.type === "divider" && (
        <div className="mt-3 space-y-2">
          <div>
            <label className="text-[10px] uppercase tracking-widest text-[#A8A398]">Stil</label>
            <select value={block.style || "dashed"} onChange={(e) => updateBlock(block.id, { style: e.target.value })}
              className="w-full mt-1 bg-[#050505] border border-[#2A2A2A] rounded px-2 py-1.5 text-sm">
              <option value="solid">Durchgezogen</option><option value="dashed">Gestrichelt</option><option value="dotted">Gepunktet</option>
            </select>
          </div>
        </div>
      )}
      {block.type === "spacer" && (
        <div className="mt-3"><RangeInput label="Höhe (px)" min={0} max={80} step={2} value={block.height || 8} onChange={(v) => updateBlock(block.id, { height: v })} /></div>
      )}
      {block.type === "image" && (
        <div className="mt-3 space-y-2">
          <div>
            <label className="text-[10px] uppercase tracking-widest text-[#A8A398]">Bild-URL (Platzhalter erlaubt)</label>
            <input value={block.url || ""} onChange={(e) => updateBlock(block.id, { url: e.target.value })}
              className="w-full mt-1 bg-[#050505] border border-[#2A2A2A] rounded px-3 py-2 text-xs font-mono focus:border-[#D4AF37] outline-none" />
          </div>
          <RangeInput label="Max. Breite (px)" min={40} max={500} step={5} value={block.max_width || 200} onChange={(v) => updateBlock(block.id, { max_width: v })} />
        </div>
      )}
      {block.type === "table" && (
        <div className="mt-3 space-y-2">
          <div>
            <label className="text-[10px] uppercase tracking-widest text-[#A8A398]">Datenquelle</label>
            <select value={block.source || "items"} onChange={(e) => updateBlock(block.id, { source: e.target.value })}
              className="w-full mt-1 bg-[#050505] border border-[#2A2A2A] rounded px-2 py-1.5 text-sm">
              <option value="items">Bestell-Positionen (items)</option>
            </select>
          </div>
          <label className="flex items-center gap-2 text-xs">
            <input type="checkbox" checked={block.show_header !== false} onChange={(e) => updateBlock(block.id, { show_header: e.target.checked })} className="accent-[#D4AF37]" />
            Kopf-Zeile anzeigen
          </label>
          <RangeInput label="Zeilenhöhe" min={10} max={30} step={1} value={block.row_height || 14} onChange={(v) => updateBlock(block.id, { row_height: v })} />
        </div>
      )}
      {block.type !== "spacer" && (
        <div className="mt-3">
          <div className="text-[10px] uppercase tracking-widest text-[#A8A398] mb-1">Ausrichtung</div>
          <div className="grid grid-cols-3 gap-1">
            {[{ v: "left", Ic: AlignLeft }, { v: "center", Ic: AlignCenter }, { v: "right", Ic: AlignRight }].map(({ v, Ic }) => (
              <button key={v} onClick={() => updateBlock(block.id, { align: v })}
                className={`py-2 flex items-center justify-center border rounded ${(block.align || "center") === v ? "border-[#D4AF37] text-[#D4AF37] bg-[#D4AF37]/[0.08]" : "border-[#2A2A2A] text-[#A8A398] hover:border-[#D4AF37]"}`}>
                <Ic size={13} />
              </button>
            ))}
          </div>
        </div>
      )}
      <div className="mt-3 grid grid-cols-2 gap-2">
        <NumberSmall label="Padding oben" value={block.padding_top || 0} onChange={(v) => updateBlock(block.id, { padding_top: v })} />
        <NumberSmall label="Padding unten" value={block.padding_bottom || 0} onChange={(v) => updateBlock(block.id, { padding_bottom: v })} />
      </div>
    </div>
  );
}

// ---- Helpers ------------------------------------------------------------

function RangeInput({ label, min, max, step, value, onChange }) {
  return (
    <div>
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <label className="text-[10px] uppercase tracking-widest text-[#A8A398]">{label}</label>
        <span className="text-xs font-mono text-[#D4AF37]">{value}</span>
      </div>
      <input type="range" min={min} max={max} step={step} value={value} onChange={(e) => onChange(Number(e.target.value))} className="w-full accent-[#D4AF37] mt-1" />
    </div>
  );
}
function ColorInput({ label, value, onChange }) {
  return (
    <div className="flex items-center gap-2">
      <input type="color" value={value || "#000000"} onChange={(e) => onChange(e.target.value)}
        className="w-11 h-9 rounded bg-transparent border border-[#2A2A2A] cursor-pointer flex-shrink-0" />
      <div className="flex-1 min-w-0">
        <div className="text-[10px] uppercase tracking-widest text-[#A8A398]">{label}</div>
        <div className="text-xs font-mono text-[#EEDC82]">{value}</div>
      </div>
    </div>
  );
}
function NumberSmall({ label, value, onChange }) {
  return (
    <div>
      <label className="text-[10px] uppercase tracking-widest text-[#A8A398]">{label}</label>
      <input type="number" min={0} max={80} value={value || 0}
        onChange={(e) => onChange(Math.max(0, Math.min(80, Number(e.target.value) || 0)))}
        className="w-full mt-1 bg-[#050505] border border-[#2A2A2A] rounded px-2 py-1 text-xs focus:border-[#D4AF37] outline-none" />
    </div>
  );
}
